import { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { apiClient } from "../../services/api";
import RefreshButton from "../RefreshButton/RefreshButton";
import "./IreneTopicMemory.css";

const MAX_CATALOG_PAGES = 25;

async function fetchCatalog(kind) {
  const items = [];
  let nextKey = null;
  for (let page = 0; page < MAX_CATALOG_PAGES; page += 1) {
    const query = nextKey
      ? `/irene-topic-memory/${kind}?limit=200&startKey=${encodeURIComponent(nextKey)}`
      : `/irene-topic-memory/${kind}?limit=200`;
    const data = await apiClient.get(query);
    items.push(...(data.items || []));
    nextKey = data.nextKey;
    if (!nextKey) break;
  }
  return items;
}

async function fetchRecentJobs() {
  const items = [];
  let nextKey = null;
  for (let page = 0; page < MAX_CATALOG_PAGES; page += 1) {
    const query = nextKey
      ? `/irene-topic-memory/jobs?limit=200&startKey=${encodeURIComponent(nextKey)}`
      : "/irene-topic-memory/jobs?limit=200";
    const data = await apiClient.get(query);
    items.push(...(data.items || []));
    nextKey = data.nextKey;
    if (!nextKey) break;
  }
  return items.sort((left, right) => {
    const leftTime = left.updatedAt || left.createdAt || "";
    const rightTime = right.updatedAt || right.createdAt || "";
    return rightTime.localeCompare(leftTime);
  });
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function StatusBadge({ status }) {
  const value = (status || "unknown").toLowerCase();
  return <i className={`topicmem-status ${value}`}>{value}</i>;
}

function MemoryEditor({ record, kind, topics, onSaved, onCancel }) {
  const field = kind === "topics" ? "topic" : "leaf";
  const id = kind === "topics" ? record.topicId : record.leafId;
  const [draft, setDraft] = useState(() => structuredClone(record[field]));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const update = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const path = `/irene-topic-memory/${kind}/${encodeURIComponent(id)}`;

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await apiClient.put(path, { expectedVersion: record.version, [field]: draft });
      await onSaved();
      onCancel();
    } catch (err) {
      setError(err.data?.details?.[0]?.error || err.data?.error || "Could not save memory. Refresh and try again.");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm(`Delete ${draft.title || id}? A revision will be kept.`)) return;
    setBusy(true);
    setError("");
    try {
      await apiClient.request(path, { method: "DELETE", body: { expectedVersion: record.version } });
      await onSaved();
      onCancel();
    } catch (err) {
      setError(err.data?.error || "Could not delete memory. Refresh and try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="topicmem-editor" onSubmit={save}>
      <label>Title<input required value={draft.title || ""} onChange={(event) => update("title", event.target.value)} /></label>
      {field === "topic" ? (
        <>
          <label>Summary<textarea required rows="3" value={draft.summary || ""} onChange={(event) => update("summary", event.target.value)} /></label>
          <label>Tags (comma-separated)<input value={(draft.tags || []).join(", ")} onChange={(event) => update("tags", event.target.value.split(",").map((tag) => tag.trim()).filter(Boolean))} /></label>
          <div className="topicmem-editor-links">
            <strong>Linked topics</strong>
            {(draft.topicLinks || []).map((link, index) => (
              <div className="topicmem-editor-link" key={index}>
                <select aria-label="Linked topic" value={link.targetTopicId} onChange={(event) => update("topicLinks", draft.topicLinks.map((item, position) => position === index ? { ...item, targetTopicId: event.target.value } : item))}>
                  {topics.filter((topic) => topic.topicId !== id).map((topic) => <option key={topic.topicId} value={topic.topicId}>{topic.topic?.title || topic.topicId}</option>)}
                </select>
                <input aria-label="Relationship" required value={link.relationship} onChange={(event) => update("topicLinks", draft.topicLinks.map((item, position) => position === index ? { ...item, relationship: event.target.value } : item))} />
                <input aria-label="Link explanation" required value={link.explanation} onChange={(event) => update("topicLinks", draft.topicLinks.map((item, position) => position === index ? { ...item, explanation: event.target.value } : item))} />
                <button type="button" className="btn-secondary btn-xs" title="Remove link" aria-label="Remove link" onClick={() => update("topicLinks", draft.topicLinks.filter((_, position) => position !== index))} disabled={busy}>×</button>
              </div>
            ))}
            {topics.length > 1 && <button type="button" className="btn-secondary btn-xs" disabled={busy} onClick={() => update("topicLinks", [...(draft.topicLinks || []), { targetTopicId: topics.find((topic) => topic.topicId !== id).topicId, relationship: "related", explanation: "Related information" }])}>Add link</button>}
          </div>
        </>
      ) : (
        <>
          <label>Content<textarea required rows="4" value={draft.content || ""} onChange={(event) => update("content", event.target.value)} /></label>
          <label>Topic<select value={draft.parentTopicId || ""} onChange={(event) => update("parentTopicId", event.target.value)}>{topics.map((topic) => <option key={topic.topicId} value={topic.topicId}>{topic.topic?.title || topic.topicId}</option>)}</select></label>
          <label>Type<input required value={draft.type || ""} onChange={(event) => update("type", event.target.value)} /></label>
          <label>Confidence<input type="number" min="0" max="1" step="0.01" required value={draft.confidence ?? ""} onChange={(event) => update("confidence", Number(event.target.value))} /></label>
          <label>Validity<select value={draft.validity?.kind || "durable"} onChange={(event) => update("validity", event.target.value === "temporary" ? { kind: "temporary", validUntil: draft.validity?.validUntil || "", reason: draft.validity?.reason || "" } : { kind: "durable" })}><option value="durable">Durable</option><option value="temporary">Temporary</option></select></label>
          {draft.validity?.kind === "temporary" && <>
            <label>Valid until (UTC)<input type="datetime-local" required value={(draft.validity.validUntil || "").slice(0, 16)} onChange={(event) => update("validity", { ...draft.validity, validUntil: `${event.target.value}:00Z` })} /></label>
            <label>Reason<input value={draft.validity.reason || ""} onChange={(event) => update("validity", { ...draft.validity, reason: event.target.value })} /></label>
          </>}
        </>
      )}
      <label>Status<input required value={draft.status || ""} onChange={(event) => update("status", event.target.value)} /></label>
      {error && <p className="topicmem-edit-error" role="alert">{error}</p>}
      <div className="topicmem-editor-actions">
        <button type="submit" disabled={busy}>Save</button>
        <button type="button" className="btn-secondary btn-xs" disabled={busy} onClick={onCancel}>Cancel</button>
        <button type="button" className="btn-secondary btn-xs topicmem-delete" disabled={busy} onClick={remove}><Trash2 size={14} aria-hidden="true" /> Delete</button>
      </div>
    </form>
  );
}

function LeafRow({ record, topics, onSaved }) {
  const leaf = record.leaf || {};
  const validity = leaf.validity || {};
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  return (
    <div className={`topicmem-leaf ${open ? "expanded" : ""}`}>
      <button
        type="button"
        className="topicmem-leaf-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span>
          <strong>{leaf.title || record.leafId}</strong>
          {!open && leaf.content && <small>{leaf.content}</small>}
        </span>
        <span className="topicmem-leaf-meta">
          {leaf.type && <i className="topicmem-type">{leaf.type}</i>}
          {validity.kind && (
            <i className={`topicmem-validity ${validity.kind}`}>
              {validity.kind}
            </i>
          )}
          <StatusBadge status={leaf.status} />
          <b aria-hidden="true">{open ? "−" : "+"}</b>
        </span>
      </button>
      {open && (
        <div className="topicmem-leaf-body">
          <button type="button" className="btn-secondary btn-xs topicmem-edit" onClick={() => setEditing(true)} title="Edit leaf"><Pencil size={14} aria-hidden="true" /> Edit</button>
          {editing ? <MemoryEditor record={record} kind="leaves" topics={topics} onSaved={onSaved} onCancel={() => setEditing(false)} /> : <>
          {leaf.content && <p>{leaf.content}</p>}
          <div className="topicmem-leaf-facts">
            <span>id <code>{record.leafId}</code></span>
            {leaf.confidence !== undefined && (
              <span>confidence {Number(leaf.confidence).toFixed(2)}</span>
            )}
            {validity.kind === "temporary" && validity.validUntil && (
              <span>valid until {formatDate(validity.validUntil)}</span>
            )}
            <span>v{record.version}</span>
            <span>updated {formatDate(record.updatedAt)}</span>
            {record.lastSessionId && <span>session <code>{record.lastSessionId}</code></span>}
          </div>
          </>}
        </div>
      )}
    </div>
  );
}

function TopicItem({ record, leaves, titlesById, topics, onSaved }) {
  const topic = record.topic || {};
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const links = topic.topicLinks || [];
  return (
    <div className={`topicmem-item ${open ? "expanded" : ""}`}>
      <button
        type="button"
        className="topicmem-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span>
          <strong>{topic.title || record.topicId}</strong>
          {!open && topic.summary && <small>{topic.summary}</small>}
        </span>
        <span className="topicmem-meta">
          <span className="topicmem-leafcount">{leaves.length} {leaves.length === 1 ? "leaf" : "leaves"}</span>
          <StatusBadge status={topic.status} />
          <b aria-hidden="true">{open ? "−" : "+"}</b>
        </span>
      </button>
      {open && (
        <div className="topicmem-body">
          <button type="button" className="btn-secondary btn-xs topicmem-edit" onClick={() => setEditing(true)} title="Edit topic"><Pencil size={14} aria-hidden="true" /> Edit</button>
          {editing ? <MemoryEditor record={record} kind="topics" topics={topics} onSaved={onSaved} onCancel={() => setEditing(false)} /> : <>
          {topic.summary && <p className="topicmem-summary">{topic.summary}</p>}
          {(topic.tags || []).length > 0 && (
            <div className="topicmem-tags">
              {topic.tags.map((tag) => <i key={tag}>{tag}</i>)}
            </div>
          )}
          <div className="topicmem-leaf-facts">
            <span>id <code>{record.topicId}</code></span>
            <span>v{record.version}</span>
            <span>updated {formatDate(record.updatedAt)}</span>
            {record.lastSessionId && <span>session <code>{record.lastSessionId}</code></span>}
          </div>
          {links.length > 0 && (
            <div className="topicmem-links">
              <h5>Linked topics</h5>
              {links.map((link, index) => (
                <div key={`${link.targetTopicId}-${index}`} className="topicmem-link">
                  <span className="topicmem-relationship">{link.relationship || "related"}</span>
                  <strong>{titlesById[link.targetTopicId] || link.targetTopicId}</strong>
                  {link.explanation && <small>{link.explanation}</small>}
                </div>
              ))}
            </div>
          )}
          <div className="topicmem-leaves">
            <h5>Leaves ({leaves.length})</h5>
            {leaves.length === 0 ? (
              <div className="topicmem-empty-row">No leaves attached to this topic.</div>
            ) : (
              leaves.map((leafRecord) => (
                <LeafRow key={leafRecord.leafId} record={leafRecord} topics={topics} onSaved={onSaved} />
              ))
            )}
          </div>
          </>}
        </div>
      )}
    </div>
  );
}

function IreneTopicMemory() {
  const [topics, setTopics] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [topicItems, leafItems, jobItems] = await Promise.all([
        fetchCatalog("topics"),
        fetchCatalog("leaves"),
        fetchRecentJobs(),
      ]);
      setTopics(topicItems.sort((left, right) => (
        (right.updatedAt || "").localeCompare(left.updatedAt || "")
      )));
      setLeaves(leafItems.sort((left, right) => (
        (right.updatedAt || "").localeCompare(left.updatedAt || "")
      )));
      setJobs(jobItems);
    } catch (err) {
      console.error("Failed to fetch irene topic memory:", err);
      setError("Could not load the linked topic memory catalog.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const titlesById = useMemo(() => Object.fromEntries(
    topics.map((record) => [record.topicId, record.topic?.title || record.topicId])
  ), [topics]);

  const latestJob = jobs[0];

  const leavesByTopic = useMemo(() => {
    const map = {};
    for (const record of leaves) {
      const parent = record.parentTopicId || record.leaf?.parentTopicId || "";
      (map[parent] = map[parent] || []).push(record);
    }
    return map;
  }, [leaves]);

  const orphanLeaves = useMemo(() => Object.entries(leavesByTopic)
    .filter(([parent]) => !titlesById[parent])
    .flatMap(([, records]) => records), [leavesByTopic, titlesById]);

  const visibleTopics = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return topics;
    return topics.filter((record) => {
      const topic = record.topic || {};
      const haystack = [
        topic.title,
        topic.summary,
        record.topicId,
        ...(topic.tags || []),
        ...(leavesByTopic[record.topicId] || []).map((l) => l.leaf?.title),
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(needle);
    });
  }, [topics, filter, leavesByTopic]);

  if (loading) {
    return <div className="placeholder">Loading linked topic memory…</div>;
  }

  if (error) {
    return (
      <div className="topicmem">
        <div className="topicmem-error">
          {error}
          <button className="btn-secondary btn-xs" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="topicmem">
      <div className="topicmem-toolbar">
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by title, tag, summary or leaf…"
          aria-label="Filter topics"
        />
        <span className="topicmem-counts">
          {topics.length} topics · {leaves.length} leaves
        </span>
        <RefreshButton onClick={load} title="Refresh topic memory" />
      </div>

      {latestJob && (
        <div className={`topicmem-activity ${latestJob.status || "unknown"}`}>
          <span>
            <strong>Last capture</strong>
            {formatDate(latestJob.updatedAt || latestJob.createdAt)}
          </span>
          <span>
            <StatusBadge status={latestJob.status} />
            {latestJob.status === "completed" && (
              <b>{latestJob.changeCount || 0} changes</b>
            )}
            {latestJob.errorType && <b>{latestJob.errorType}</b>}
          </span>
        </div>
      )}

      {topics.length === 0 ? (
        <div className="placeholder">
          No topics yet. Luna creates them after Irene finishes sessions.
        </div>
      ) : visibleTopics.length === 0 ? (
        <div className="placeholder">No topics match “{filter}”.</div>
      ) : (
        <div className="topicmem-list">
          {visibleTopics.map((record) => (
            <TopicItem
              key={record.topicId}
              record={record}
              leaves={leavesByTopic[record.topicId] || []}
              titlesById={titlesById}
              topics={topics}
              onSaved={load}
            />
          ))}
        </div>
      )}

      {orphanLeaves.length > 0 && (
        <div className="topicmem-orphans">
          <h5>Leaves without a visible parent topic ({orphanLeaves.length})</h5>
          {orphanLeaves.map((record) => (
            <LeafRow key={record.leafId} record={record} />
          ))}
        </div>
      )}
    </div>
  );
}

export default IreneTopicMemory;
